import { BackendError } from "@exceptions";
import {
  Provider,
  createContext as createContextReact,
  useContext as useReactContext,
} from "react";

export function getErrorMessage(error: unknown) {
  if (!error) {
    return "";
  }

  if (error instanceof BackendError) {
    return error.message;
  }

  return "An error occurred";
}

export function currencyFormat(
  value: number,
  currency: string,
  language: string = "en-US",
) {
  return new Intl.NumberFormat(language, {
    style: "currency",
    currency,
  }).format(value);
}

type FormObject = {
  [key: string]: string | string[];
};

export function serializeFormQueryLegacy(
  formObject: FormObject,
): URLSearchParams {
  const params = new URLSearchParams();

  for (const key in formObject) {
    // eslint-disable-next-line no-prototype-builtins
    if (formObject.hasOwnProperty(key)) {
      const value = formObject[key];
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v));
      } else {
        params.append(key, value);
      }
    }
  }

  return params;
}

export function serializeTableFilter(
  data: Record<string, string>,
): URLSearchParams {
  const params = new URLSearchParams();

  const filterArray: { field: string; values: string[] }[] = [];
  for (const key in data) {
    if (
      // eslint-disable-next-line no-prototype-builtins
      data.hasOwnProperty(key) &&
      data[key] !== undefined &&
      data[key] !== ""
    ) {
      const values = data[key].split(",").map((v) => v.trim());
      if (values.length > 0) {
        filterArray.push({ field: key, values });
      }
    }
  }

  let filterString = "";
  for (let i = 0; i < filterArray.length; i++) {
    const filter = filterArray[i];
    if (i > 0) {
      filterString += "|";
    }
    filterString += `${filter.field}:${filter.values.join(",")}`;
  }

  if (filterString) {
    params.append("filter", filterString);
  }

  return params;
}

export function debounce<T extends (...args: any[]) => unknown>(
  func: T,
  wait: number,
) {
  let timeout: number;
  return (...args: Parameters<T>): void => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

type ContextCreateResult<ContextType> = {
  Provider: Provider<ContextType | undefined>;
  useContext: () => ContextType;
};

export function createContext<ContextType>(): ContextCreateResult<ContextType> {
  const context = createContextReact<ContextType | undefined>(undefined);

  function useContext() {
    const contextValue = useReactContext(context);

    if (!contextValue) {
      throw new Error("useContext must be inside a Provider with a value");
    }
    return contextValue;
  }
  return { Provider: context.Provider, useContext };
}
